"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import { cn } from "../lib/utils";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

const CalendlyButton = dynamic(() => import("./calendly"), {
  ssr: false,
});

const ACCESS_KEY = "cf9c84e5-95f4-46ee-a26a-bf7512e27c56";

const CALL_VOLUMES = [
  "Under 500 calls / month",
  "500 – 2,000 calls / month",
  "2,000 – 5,000 calls / month",
  "5,000+ calls / month",
];

const ContactUs = ({ short = false }) => {
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isAIPage = pathname === "/ai";
  const isContactPage = pathname?.includes("/contact");
  const t = useTranslations("contactForm");
  const tHome = useTranslations("home");
  const models = tHome.raw("models");
  const budgets = t.raw("budgets");
  const aiMaturityOptions = [
    "Exploring AI",
    "Early Stage (1–2 pilots)",
    "Scaling AI",
    "AI-Mature",
  ];
  const selectedModel = searchParams.get("model");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    // console.log(e.target)
    const formData = new FormData(e.target);
// console.log(formData)
    formData.append("access_key", ACCESS_KEY);

    try {
      // Build separate payloads so the same FormData isn't consumed twice
      const dbData = new FormData();
      const w3Data = new FormData();
      for (const [key, value] of formData.entries()) {
        dbData.append(key, value);
        w3Data.append(key, value);
      }

      // Save to D1 database
      const res = await fetch("/api/contact", {
        method: "POST",
        body: dbData,
      }).then((res) => res.json());

      // Web3Forms email notifications (fire-and-forget)
      if (!(process.env.NODE_ENV === "development")) {
        fetch("https://api.web3forms.com/submit", {
          method: "POST",
          body: w3Data,
        }).catch(() => {});
      }

      if (res.success) {
        setSuccess(true);
        setIsSending(false);
        setTimeout(() => {
          router.push("/");
        }, 2000);
      } else {
        setIsSending(false);
        setError(res.error || "Error saving submission.");
      }
    } catch (error) {
      setIsSending(false);
      setError("Error occured while sending message. Try again.");
    }
  };


  const inputCls = "bg-zinc-800/60 border border-white/10 text-white rounded-lg p-2.5 mt-2 w-full focus:outline-none focus:ring-1 focus:ring-violet-500/60 focus:border-violet-500/40 transition-colors placeholder:text-zinc-600";
  const selectCls = "bg-zinc-800/60 border border-white/10 text-zinc-200 rounded-lg p-2.5 focus:outline-none focus:ring-1 focus:ring-violet-500/60 transition-colors";
  const labelCls = "text-zinc-300 font-medium text-sm";

  return (
    <div className="p-6 bg-zinc-900/60 border border-white/[0.07] rounded-2xl">
      <form
        onSubmit={handleSubmit}
        className={cn(
          "relative w-full max-w-2xl flex",
          "flex-col items-start justify-start",
        )}>
        <div className="flex flex-col w-full">
          <label htmlFor="name" className={labelCls}>
            {t("name")} <span className="text-red-400">*</span>
          </label>
          <input
            type="name"
            name="name"
            required
            id="name"
            className={inputCls}
          />
        </div>
        <div className="flex flex-col w-full mt-5">
          <label htmlFor="email" className={labelCls}>
            {t("email")} <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            name="email"
            required
            id="email"
            className={inputCls}
          />
        </div>
        {!short && (
          <div className="flex flex-col w-full mt-5">
            <label htmlFor="contact_number" className={labelCls}>
              {t("phone")}
            </label>
            <input
              type="tel"
              name="contact_number"
              id="contact_number"
              maxLength={50}
              className={inputCls}
            />
          </div>
        )}
        {!isAIPage && !short && (
          <>
            <div className="flex flex-wrap w-full justify-between gap-4 mt-5">
              <div className="flex flex-col gap-2 flex-1 min-w-[140px]">
                <label htmlFor="intention" className={labelCls}>
                  {t("intention")}
                </label>
                <select
                  defaultValue={selectedModel}
                  name="intention"
                  id="intention"
                  className={cn(selectCls, "w-full")}>
                  {models.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                  <option value={t("saySomethingElse")}>{t("saySomethingElse")}</option>
                </select>
              </div>
              <div className="flex flex-col gap-2 flex-1 min-w-[140px]">
                <label htmlFor="budget" className={labelCls}>
                  {t("budget")}
                </label>
                <select
                  name="budget"
                  id="budget"
                  className={cn(selectCls, "w-full")}>
                  {budgets.map((budget) => (
                    <option key={budget} value={budget}>
                      {budget}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-5 w-full">
              <label htmlFor="ai_maturity" className={labelCls}>
                {t("aiMaturity")}
              </label>
              <select
                name="ai_maturity"
                id="ai_maturity"
                className={cn(selectCls, "w-full")}>
                {aiMaturityOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-full mt-5">
              <label htmlFor="message" className={labelCls}>
                {t("message")}
              </label>
              <textarea
                name="message"
                id="message"
                rows={4}
                className={inputCls}
              />
            </div>
          </>
        )}
        {!isAIPage && short && !isContactPage && (
          <>
            <div className="flex flex-col w-full mt-5">
              <label htmlFor="call_volume" className={labelCls}>
                Monthly call volume
              </label>
              <select
                name="call_volume"
                id="call_volume"
                className={cn(selectCls, "w-full mt-2")}>
                {CALL_VOLUMES.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-full mt-5">
              <label htmlFor="message" className={labelCls}>
                Anything else we should know? <span className="text-zinc-500 font-normal">(optional)</span>
              </label>
              <textarea
                name="message"
                id="message"
                rows={3}
                className={inputCls}
              />
            </div>
          </>
        )}
        {!isAIPage && short && isContactPage && (
          <div className="flex flex-col w-full mt-5">
            <label htmlFor="message" className={labelCls}>
              What are you looking to build? <span className="text-zinc-500 font-normal">(optional)</span>
            </label>
            <textarea
              name="message"
              id="message"
              rows={3}
              placeholder="Briefly describe your AI challenge or goal..."
              className={inputCls}
            />
          </div>
        )}
        {!isAIPage && !short && (
          <>
            <p className="text-zinc-500 text-sm mt-5">
              {t("consentText")}
            </p>
            <div className="flex gap-2 mt-2">
              <input
                type="radio"
                name="consent"
                id="consent"
                className="w-5 h-5 mt-0.5 accent-violet-500"
              />
              <label htmlFor="consent" className="text-zinc-400 text-sm">{t("consentYes")}</label>
            </div>
          </>
        )}
        {success && (
          <p className="mt-4 p-3 w-full border border-green-500/30 bg-green-500/10 text-green-400 rounded-lg text-sm">
            {t("successMsg")}
          </p>
        )}
        {error && (
          <p className="mt-4 p-3 w-full border border-red-500/30 bg-red-500/10 text-red-400 rounded-lg text-sm">
            {t("errorMsg")}
          </p>
        )}
        <button
          type="submit"
          disabled={isSending}
          className={cn(
            "w-full px-6 py-3 text-base rounded-xl font-medium transition-all duration-200",
            "inline-flex items-center justify-center gap-2 my-4",
            "bg-gradient-to-r from-violet-600 to-cyan-500 text-white",
            "shadow-lg shadow-violet-500/20",
            "hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]",
            "disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100",
          )}>
          {isSending ? (
            <svg
              className="fill-white"
              width="24"
              height="24"
              viewBox="0 0 24 24">
              <circle cx="4" cy="12" r="3">
                <animate
                  id="spinner_qFRN"
                  begin="0;spinner_OcgL.end+0.25s"
                  attributeName="cy"
                  calcMode="spline"
                  dur="0.6s"
                  values="12;6;12"
                  keySplines=".33,.66,.66,1;.33,0,.66,.33"
                />
              </circle>
              <circle cx="12" cy="12" r="3">
                <animate
                  begin="spinner_qFRN.begin+0.1s"
                  attributeName="cy"
                  calcMode="spline"
                  dur="0.6s"
                  values="12;6;12"
                  keySplines=".33,.66,.66,1;.33,0,.66,.33"
                />
              </circle>
              <circle cx="20" cy="12" r="3">
                <animate
                  id="spinner_OcgL"
                  begin="spinner_qFRN.begin+0.2s"
                  attributeName="cy"
                  calcMode="spline"
                  dur="0.6s"
                  values="12;6;12"
                  keySplines=".33,.66,.66,1;.33,0,.66,.33"
                />
              </circle>
            </svg>
          ) : (
            t("submit")
          )}
        </button>
        {!isAIPage && (
          <>
            <div className="relative flex items-center gap-3 py-2 w-full">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs font-medium text-zinc-500 uppercase tracking-widest">
                {t("or")}
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <CalendlyButton className={"w-full py-3"} />
            <p className="text-zinc-600 text-xs mt-4">
              {t("requiredNote")}
            </p>
          </>
        )}
        <p className="text-zinc-600 text-xs mt-3">
          {t("sensitiveNote")}
        </p>
      </form>
    </div>
  );
};

export default ContactUs;
